import {
    Injectable
} from "@angular/core";
import {
    Observable, of
} from "rxjs";
import {
    Unit
} from "@shopliftr/common-js/shared";
import {
    Language,
    ResourceType,
    EnumUtil,
    LocalizationUtil
} from "@shopliftr/common-js/data";
import { IAutocompleteController } from "../../interfaces/autocomplete-controller";
import { IAutocompleteOption } from "../../interfaces/autocomplete-option";
import { IAutocompleteResults } from "../../interfaces/autocomplete-results";
import {
    ArrayUtil, StringUtil
} from "@shopliftr/common-js/data";
import { UomAutocompleteControllerOptions } from "./uom-autocomplete-controller-options";

/**
 * Autocomplete controller for Units of Measure (UOM) that provides search and display functionality
 * for unit types with multi-language support.
 *
 * This service loads UOM resources (short and long forms) from the ResourceService and provides
 * autocomplete functionality for selecting units. Display format: "long form (short form)"
 * Example: "pound (lb)", "kilogram (kg)"
 *
 * Usage:
 * ```typescript
 * constructor() {
 *   private readonly uomController = inject(UomAutocompleteControllerService);
 * }
 *
 * searchUnits() {
 *   this.uomController.autocomplete$("pound", { language: Language.English })
 *     .subscribe(results => console.log(results.options));
 * }
 * ```
 */
@Injectable({
    providedIn: "root"
})
export class UomAutocompleteControllerService implements IAutocompleteController<Unit, UomAutocompleteControllerOptions> {

    /**
     * Resource Map Cache holds a map for a language. Each Language Map has a Map<unit, string> which holds the
     * unit as the key and value is the display name for the UOM Unit in the format "Long UOM Name (Short UOM Name")"
     * @private
     * @memberof UomAutocompleteControllerService
     */
    private readonly _resourceMap = new Map<Language, Map<Unit, string>>();

    /**
     * autocomplete$ function returns an observable of IAutocompleteResults given a query string and options (language).
     * @param query string to filter UOM options
     * @param options contains language
     * @returns Autocomplete Options of type IAutocompleteResults
     */
    autocomplete$(query?: string, options?: UomAutocompleteControllerOptions): Observable<IAutocompleteResults<Unit>> {
        const language: Language = options?.language || Language.English;

        let filteredResults: Map<Unit, string> = this._getLocalizedMap(language);
        const sanitizedQuery = StringUtil.sanitizeString(query);
        // Only filter if filteredResults sanitizedQuery has a value
        if (sanitizedQuery && filteredResults) {
            const queryLower = sanitizedQuery.toLowerCase();
            filteredResults = new Map(
                Array.from(filteredResults.entries()).filter(([unit, value]) =>
                    value.toLowerCase().includes(queryLower))
            );
        }
        const autocompleteOptions = new Array<IAutocompleteOption<Unit>>();
        if (filteredResults) {
            filteredResults.forEach((mapValue, unit) => {
                const autocompleteItem: IAutocompleteOption<Unit> = {
                    option: mapValue,
                    value: unit
                };
                autocompleteOptions.push(autocompleteItem);
            });
        }

        let autocompleteResults: IAutocompleteResults<Unit> = {
            options: autocompleteOptions
        };

        return of(autocompleteResults);
    }

    /**
     * Returns the display name of the UOM selection in the format of Long UOM Name (Short UOM Name )
     * @param value Unit
     * @param options Language
     * @returns d name in the format Long UOM Name (Short UOM Name ) as string
     */
    getAutocompleteDisplayName(value?: Unit, options?: any): string {
        const language: Language = options?.language || Language.English;
        const mapValue = this._getLocalizedMap(language)?.get(value);
        return mapValue || value || "";
    }

    /**
     * trackByIdentifier function returns the value of IAutocompleteOption which should be unique
     * @param index of item
     * @param result unique identifier value
     * @returns unique identifier value
     */
    trackByIdentifier(index: number, result: IAutocompleteOption<Unit>): any {
        return result?.value;
    }

    /**
     * Function that determines if one value is unique among an Array of items.
     * @param value Unit
     * @param items Array of Units
     * @returns true if value is not in the items array, false otherwise
     */
    isUnique(value: Unit, items: Array<Unit>): boolean {
        return !ArrayUtil.contains(value, items);
    }

    /**
     * Ensures that the resource maps have been loaded given a language, if not they are fetched through the
     * loadResources method and getResources and builds the map for this language.
     * Returns true when map is generated and cached.
     * Returns false if there is a problem retrieving map from ResourceService
     * @param language
     * @returns boolean true if the map has been loaded and cached.
     * False if there was a problem created the map
     */
    private _getLocalizedMap(language: Language): Map<Unit, string> {
        let map = this._resourceMap.get(language);
        if (!map) {
            map = new Map<Unit, string>();
            this._resourceMap.set(language, map);
            EnumUtil.getValues(Unit).forEach((unit) => {
                const uomShort = LocalizationUtil.localizeResource(unit, ResourceType.UomShort, language);
                const uomLong = LocalizationUtil.localizeResource(unit, ResourceType.UomLong, language);
                map.set(unit, `${uomLong} (${uomShort})`);
            });
        }
        return map;
    }
}
